import React, { useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { Calendar, Clock, ArrowLeft, Share2 } from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { getBlogPost, getRelatedPosts, BlogPost as BlogPostType } from '@/content/blog/posts';
import { PamPostByline, PamSignoff, isPamAuthored } from '@/components/blog/PamByline';
import DiagnosticCTA from '@/components/blog/DiagnosticCTA';
import { buildFAQPageSchema } from '@/seo/schema';

/**
 * Strip the first H1 heading and optional italic subtitle from markdown content.
 * The article header already displays title/excerpt, so we don't need them duplicated.
 */
const stripTitleFromContent = (content: string): string => {
  const lines = content.split('\n');
  let startIndex = 0;

  // Skip leading empty lines
  while (startIndex < lines.length && lines[startIndex].trim() === '') {
    startIndex++;
  }

  // Check if first non-empty line is an H1
  if (startIndex < lines.length && lines[startIndex].trim().startsWith('# ')) {
    startIndex++;

    // Skip empty line after H1 if present
    if (startIndex < lines.length && lines[startIndex].trim() === '') {
      startIndex++;
    }

    // Check if next line is italic text (subtitle/excerpt) - starts and ends with *
    if (startIndex < lines.length) {
      const nextLine = lines[startIndex].trim();
      if (nextLine.startsWith('*') && nextLine.endsWith('*') && !nextLine.startsWith('**')) {
        startIndex++;
      }
    }
  }

  return lines.slice(startIndex).join('\n').trim();
};

const formatDate = (dateString: string): string => {
  // Append T00:00:00 to treat YYYY-MM-DD as local time, not UTC
  // This prevents dates from appearing as the previous day in US timezones
  const date = new Date(`${dateString}T00:00:00`);
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });
};

const RelatedPostCard: React.FC<{ post: BlogPostType }> = ({ post }) => (
  <Link
    to={`/blog/${post.slug}`}
    className="group block p-6 bg-white rounded-2xl border-2 border-neutral-200 shadow-ledge hover:border-primary-300 transition-colors"
  >
    <h3 className="font-display font-semibold text-lg text-neutral-900 group-hover:text-primary-700 transition-colors mb-2">
      {post.title}
    </h3>
    <p className="text-sm text-neutral-600 line-clamp-2 mb-3">
      {post.excerpt}
    </p>
    <div className="flex items-center gap-2 text-xs text-neutral-500">
      <Clock className="w-3.5 h-3.5" />
      {post.readTime} min read
    </div>
  </Link>
);

// The reading measure — a column, styled entirely on .pen tokens (teal primary,
// warm neutrals, hard ledges, Fraunces headings). No raw palette, no blur.
const PROSE_CLASSES = [
  'prose prose-lg max-w-none',
  'prose-headings:font-display prose-headings:text-neutral-900 prose-headings:tracking-tight',
  'prose-h2:text-2xl prose-h2:mt-12 prose-h2:mb-4',
  'prose-h3:text-xl prose-h3:mt-8 prose-h3:mb-3',
  'prose-p:text-neutral-700 prose-p:leading-relaxed',
  'prose-a:text-primary-700 prose-a:font-semibold prose-a:no-underline hover:prose-a:underline',
  'prose-strong:text-neutral-900',
  'prose-ul:text-neutral-700 prose-ol:text-neutral-700',
  'prose-li:marker:text-primary-500',
  'prose-blockquote:border-l-4 prose-blockquote:border-primary-500 prose-blockquote:bg-primary-50',
  'prose-blockquote:rounded-r-xl prose-blockquote:py-2 prose-blockquote:px-5 prose-blockquote:not-italic prose-blockquote:text-neutral-800 prose-blockquote:font-normal',
  'prose-table:border-2 prose-table:border-neutral-200 prose-table:rounded-xl prose-table:overflow-hidden prose-table:shadow-ledge-sm',
  'prose-th:bg-primary-50 prose-th:text-neutral-900 prose-th:font-display prose-th:p-3',
  'prose-td:p-3 prose-td:border-t prose-td:border-neutral-200 prose-td:text-neutral-700',
  'prose-hr:border-neutral-200',
  'prose-code:text-primary-700',
].join(' ');

const BlogPost: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const post = slug ? getBlogPost(slug) : undefined;

  // Topically related letters (keyword overlap, recency fallback) — not just
  // the three newest, which used to be identical under every article.
  const relatedPosts = slug ? getRelatedPosts(slug, 3) : [];

  useEffect(() => {
    // Scroll to top when post changes
    window.scrollTo(0, 0);
  }, [slug]);

  const handleShare = async () => {
    if (navigator.share && post) {
      try {
        await navigator.share({
          title: post.title,
          text: post.excerpt,
          url: window.location.href,
        });
      } catch (err) {
        // User cancelled or share failed - fail silently
      }
    } else if (navigator.clipboard?.writeText) {
      // Fallback: copy to clipboard (with feature check for browser compatibility)
      navigator.clipboard.writeText(window.location.href);
    }
  };

  if (!post) {
    return (
      <div className="min-h-screen bg-neutral-50 flex items-center justify-center px-4">
        {/* A client-routed SPA answers HTTP 200 for ANY /blog/<slug>, so without
            this an unknown slug is a soft 404: indexable, and inheriting the
            homepage's title because this branch rendered no Helmet at all.
            GA4 shows real crawlers hitting invented slugs (three base64-encoded
            ones in 14 days), so this is not hypothetical. noindex is what stops
            them being indexed; returning a true 404 status needs filesystem-first
            routing in vercel.json, which is a separate change. */}
        <Helmet>
          <title>Post not found · Social Work Test Prep Academy</title>
          <meta name="robots" content="noindex, follow" />
          <link rel="canonical" href="https://www.socialworktestprepacademy.com/blog" />
        </Helmet>
        <div className="text-center">
          <h1 className="heading-2 text-neutral-900 mb-4">
            Post Not Found
          </h1>
          <p className="body-base text-neutral-600 mb-6">
            The letter you're looking for doesn't exist.
          </p>
          <Link to="/blog" className="btn-primary">
            <ArrowLeft className="w-4 h-4" />
            Back to Pam's Column
          </Link>
        </div>
      </div>
    );
  }

  const pam = isPamAuthored(post.author);

  const faqSchema = post.faqs?.length ? buildFAQPageSchema(post.faqs) : null;

  const schemaData = {
    "@context": "https://schema.org",
    "@type": "BlogPosting",
    "headline": post.title,
    "description": post.excerpt,
    "datePublished": post.publishedAt,
    "author": {
      "@type": "Person",
      "name": post.author
    },
    "publisher": {
      "@type": "Organization",
      "name": "Social Work Test Prep Academy",
      "url": "https://www.socialworktestprepacademy.com"
    }
  };

  return (
    <>
      <Helmet>
        <title>{post.title} | Pam's Column</title>
        <meta name="description" content={post.excerpt} />
        <meta property="og:title" content={post.title} />
        <meta property="og:description" content={post.excerpt} />
        <meta property="og:type" content="article" />
        <meta property="og:url" content={`https://www.socialworktestprepacademy.com/blog/${post.slug}`} />
        <meta property="og:image" content="https://www.socialworktestprepacademy.com/images/blog-og-image.png" />
        <meta property="og:site_name" content="Social Work Test Prep Academy" />
        <meta property="article:published_time" content={post.publishedAt} />
        <meta property="article:author" content={post.author} />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={post.title} />
        <meta name="twitter:description" content={post.excerpt} />
        <meta name="twitter:image" content="https://www.socialworktestprepacademy.com/images/blog-og-image.png" />
        <link rel="canonical" href={`https://www.socialworktestprepacademy.com/blog/${post.slug}`} />
        <script type="application/ld+json">
          {JSON.stringify(schemaData)}
        </script>
        {faqSchema && (
          <script type="application/ld+json">
            {JSON.stringify(faqSchema)}
          </script>
        )}
      </Helmet>

      <div className="min-h-screen bg-neutral-50">
        {/* Article header — the reading measure */}
        <div className="max-w-2xl mx-auto px-4 sm:px-6 pt-8 md:pt-12">
          <nav className="mb-8">
            <Link
              to="/blog"
              className="inline-flex items-center gap-2 text-sm font-semibold text-primary-700 hover:underline"
            >
              <ArrowLeft className="w-4 h-4" />
              Pam's Column
            </Link>
          </nav>

          <header>
            <p className="eyebrow mb-4 text-primary-600">
              {pam ? 'A letter from Pam' : 'From the SWTPA team'}
            </p>

            <h1 className="font-display font-bold tracking-tight text-neutral-900 text-4xl md:text-5xl leading-tight mb-8">
              {post.title}
            </h1>

            <div className="flex flex-wrap items-center justify-between gap-4 pb-8 border-b-2 border-neutral-200">
              <PamPostByline author={post.author} />
              <div className="flex items-center gap-4 text-sm text-neutral-500">
                <span className="flex items-center gap-1.5">
                  <Calendar className="w-4 h-4" />
                  {formatDate(post.publishedAt)}
                </span>
                <span className="flex items-center gap-1.5">
                  <Clock className="w-4 h-4" />
                  {post.readTime} min read
                </span>
                <button
                  onClick={handleShare}
                  className="flex items-center gap-1.5 text-primary-700 hover:underline font-semibold"
                  title="Share this letter"
                >
                  <Share2 className="w-4 h-4" />
                  Share
                </button>
              </div>
            </div>
          </header>
        </div>

        {/* Content */}
        <article className="max-w-2xl mx-auto px-4 sm:px-6 py-10">
          {/* The answer, before the setup. Written to survive being quoted with
              no surrounding page — which is exactly how an answer engine uses it. */}
          {post.keyTakeaway && (
            <aside
              data-testid="blog-key-takeaway"
              className="mb-8 rounded-2xl border-2 border-primary-200 bg-primary-50 p-5 sm:p-6"
              style={{ boxShadow: '0 4px 0 rgba(27,58,56,0.08)' }}
            >
              <p className="eyebrow mb-2">The short answer</p>
              <p className="text-base sm:text-lg text-neutral-800 leading-relaxed">
                {post.keyTakeaway}
              </p>
            </aside>
          )}

          <div className={PROSE_CLASSES}>
            <ReactMarkdown remarkPlugins={[remarkGfm]}>
              {stripTitleFromContent(post.content)}
            </ReactMarkdown>
          </div>

          {post.faqs && post.faqs.length > 0 && (
            <section className="mt-12" data-testid="blog-faq">
              <h2 className="font-display font-bold text-2xl text-neutral-900 mb-5">
                Questions people ask about this
              </h2>
              <ul className="space-y-5 list-none pl-0">
                {post.faqs.map(({ q, a }) => (
                  <li key={q} className="rounded-xl border-2 border-neutral-200 bg-white p-5">
                    <h3 className="font-bold text-neutral-900 mb-2">{q}</h3>
                    <p className="text-neutral-700 leading-relaxed">{a}</p>
                  </li>
                ))}
              </ul>
            </section>
          )}

          {/* Hand-signed sign-off — the warmth of the email letters */}
          <PamSignoff author={post.author} />

          {/* The single diagnostic CTA — no mid-article interruptions */}
          <div className="mt-12">
            <DiagnosticCTA campaign="blog_cta" />
          </div>
        </article>

        {/* More from the column */}
        {relatedPosts.length > 0 && (
          <div className="max-w-5xl mx-auto px-4 sm:px-6 py-12 md:py-16 border-t-2 border-neutral-200">
            <h2 className="font-display font-bold text-2xl text-neutral-900 mb-8 text-center">
              More from the column
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {relatedPosts.map((relatedPost) => (
                <RelatedPostCard key={relatedPost.slug} post={relatedPost} />
              ))}
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default BlogPost;
